import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Book as BookIcon, Library, Brain, Users, UserCircle, Compass } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { AnimatedIcon, IconMotion } from './AnimatedIcon';

export const BottomNavigationBar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();

  const isVisible = ['/', '/roadmap', '/library-hub', '/library', '/study-hub', '/community-hub', '/community-feed', '/personal-hub'].includes(location.pathname) || location.pathname.startsWith('/profile');

  const currentPath = location.pathname;

  // Cada item tem sua própria micro-animação ao ser selecionado
  const navItems: { id: string; path: string; icon: React.ElementType; motion: IconMotion; label: string }[] = [
    { id: 'home', path: '/', icon: BookIcon, motion: 'flip', label: t('hubs.home', 'Início') },
    { id: 'roadmap', path: '/roadmap', icon: Compass, motion: 'spin', label: t('hubs.roadmap', 'Trilha') },
    { id: 'study', path: '/study-hub', icon: Brain, motion: 'pulse', label: t('hubs.study.title', 'Estudo') },
    { id: 'library', path: '/library-hub', icon: Library, motion: 'slide', label: t('hubs.library.title', 'Biblioteca') },
    { id: 'community', path: '/community-hub', icon: Users, motion: 'bounce', label: t('hubs.community.title', 'Comunidade') },
    { id: 'personal', path: '/personal-hub', icon: UserCircle, motion: 'wobble', label: t('hubs.personal.title', 'Pessoal') },
  ];

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.nav
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 24 }}
          transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
          className="fixed bottom-0 left-0 right-0 z-50 flex justify-center px-4 pb-4 pb-safe pointer-events-none"
        >
          <div className="pointer-events-auto flex items-center gap-1 p-1.5 rounded-full bg-surface/90 backdrop-blur-xl border border-black/[0.06] dark:border-white/[0.06] shadow-[0_12px_40px_-12px_rgba(20,20,19,0.25)] dark:shadow-[0_12px_40px_-12px_rgba(0,0,0,0.7)]">
            {navItems.map((item) => {
              const isActive = item.id === 'personal'
                ? currentPath === '/personal-hub' || currentPath.startsWith('/profile') || currentPath === '/settings' || currentPath === '/achievements' || currentPath === '/reports'
                : item.id === 'community'
                ? currentPath === '/community-hub' || currentPath.startsWith('/community') || currentPath === '/chat' || currentPath === '/friends' || currentPath === '/book-club'
                : item.id === 'library'
                ? currentPath === '/library-hub' || currentPath === '/library' || currentPath.startsWith('/audiobooks') || currentPath === '/libritui-library'
                : currentPath === item.path;
              const Icon = item.icon;

              return (
                <motion.button
                  key={item.id}
                  layout
                  onClick={() => navigate(item.path)}
                  transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                  className={`relative flex items-center justify-center h-11 rounded-full cursor-pointer shrink-0 outline-none focus-visible:ring-2 focus-visible:ring-primary/50 ${
                    isActive ? 'px-4 gap-2' : 'w-11'
                  }`}
                  title={item.label}
                  aria-label={item.label}
                  aria-current={isActive ? 'page' : undefined}
                >
                  {isActive && (
                    <motion.span
                      layoutId="bottomNavPill"
                      className="absolute inset-0 rounded-full bg-ink"
                      transition={{ type: 'spring', stiffness: 420, damping: 34 }}
                    />
                  )}
                  <span className={`relative z-10 flex items-center justify-center transition-colors duration-300 ${isActive ? 'text-surface' : 'text-muted hover:text-ink'}`}>
                    <AnimatedIcon motion={item.motion} active={isActive}>
                      <Icon size={20} strokeWidth={1.75} />
                    </AnimatedIcon>
                  </span>
                  <AnimatePresence initial={false}>
                    {isActive && (
                      <motion.span
                        key="label"
                        initial={{ opacity: 0, width: 0 }}
                        animate={{ opacity: 1, width: 'auto' }}
                        exit={{ opacity: 0, width: 0 }}
                        transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                        className="relative z-10 text-[13px] font-medium text-surface whitespace-nowrap overflow-hidden"
                      >
                        {item.label}
                      </motion.span>
                    )}
                  </AnimatePresence>
                </motion.button>
              );
            })}
          </div>
        </motion.nav>
      )}
    </AnimatePresence>
  );
};
